import React, { useMemo } from "react";
import { OverlayView, OverlayViewF } from "@react-google-maps/api";
import { dt } from "../../../../shared/themes/redesignTokens";

// ===================================================================
//  PRIORITY COLORS
// ===================================================================

export const PRIORITY_COLORS: Record<number, string> = {
  1: "#E74C3C", // Urgent
  2: "#E67E22", // High
  3: "#2980B9", // Medium
  4: "#27AE60", // Low
};

export const getPriorityColor = (code: number): string =>
  PRIORITY_COLORS[code] || PRIORITY_COLORS[3];

// ===================================================================
//  MARKER PULSE KEYFRAME (injected once)
// ===================================================================

const PULSE_KEYFRAME_ID = "rfs-marker-pulse-kf";
const injectPulseKeyframe = () => {
  if (document.getElementById(PULSE_KEYFRAME_ID)) return;
  const style = document.createElement("style");
  style.id = PULSE_KEYFRAME_ID;
  style.textContent = `
    @keyframes rfsMarkerPulse {
      0%   { transform: scale(1); opacity: 0.7; }
      50%  { transform: scale(1.4); opacity: 0; }
      100% { transform: scale(1); opacity: 0; }
    }
    @keyframes rfsDistanceFadeIn {
      0%   { opacity: 0; transform: translateY(4px); }
      100% { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
};

// ===================================================================
//  RFS OWNER MARKER
// ===================================================================

const DEFAULT_SIZE = 40;

export interface RfsOwnerMarkerProps {
  position: { lat: number; lng: number };
  companyName: string;
  logoUrl?: string;
  priorityCode: number;
  onClick?: () => void;
  isSelected?: boolean;
  size?: number;
}

const RfsOwnerMarker: React.FC<RfsOwnerMarkerProps> = React.memo(
  ({ position, companyName, logoUrl, priorityCode, onClick, isSelected, size }) => {
    const markerSize = size || DEFAULT_SIZE;
    const color = getPriorityColor(priorityCode);
    const hasUrgentOrHigh = priorityCode === 1 || priorityCode === 2;
    const firstLetter = companyName?.charAt(0)?.toUpperCase() || "?";
    const isClickable = !!onClick;

    const pos = useMemo(
      () => ({ lat: position.lat, lng: position.lng }),
      [position.lat, position.lng]
    );

    const getPixelPositionOffset = useMemo(
      () => () => ({ x: -(markerSize / 2), y: -(markerSize / 2 + 10) }),
      [markerSize]
    );

    injectPulseKeyframe();

    const paneName = isClickable
      ? OverlayView.OVERLAY_MOUSE_TARGET
      : OverlayView.OVERLAY_LAYER;

    return (
      <OverlayViewF
        position={pos}
        mapPaneName={paneName}
        getPixelPositionOffset={getPixelPositionOffset}
      >
        <div
          onClick={isClickable ? onClick : undefined}
          style={{
            position: "relative",
            width: markerSize,
            height: markerSize,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pointerEvents: isClickable ? "auto" : "none",
            cursor: isClickable ? "pointer" : "default",
            transition: "transform 0.2s ease",
            transform: isSelected ? "scale(1.1)" : "scale(1)",
          }}
          onMouseEnter={(e) => {
            if (isClickable && !isSelected) {
              (e.currentTarget as HTMLDivElement).style.transform = "scale(1.08)";
            }
          }}
          onMouseLeave={(e) => {
            if (isClickable && !isSelected) {
              (e.currentTarget as HTMLDivElement).style.transform = "scale(1)";
            }
          }}
        >
          {/* Pulse ring for urgent/high */}
          {hasUrgentOrHigh && (
            <div
              style={{
                position: "absolute",
                width: markerSize,
                height: markerSize,
                borderRadius: "50%",
                border: `2px solid ${color}`,
                animation: "rfsMarkerPulse 2.5s ease-in-out infinite",
                top: 0,
                left: 0,
              }}
            />
          )}

          {/* Main circle: colored ring with white inner */}
          <div
            style={{
              width: markerSize,
              height: markerSize,
              borderRadius: "50%",
              background: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: isSelected
                ? `0 0 0 3px ${color}40, 0 2px 8px rgba(0,0,0,0.25)`
                : "0 2px 8px rgba(0,0,0,0.25)",
              transition: "box-shadow 0.2s ease",
            }}
          >
            <div
              style={{
                width: markerSize - 6,
                height: markerSize - 6,
                borderRadius: "50%",
                backgroundColor: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=""
                  style={{
                    width: markerSize - 8,
                    height: markerSize - 8,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <span
                  style={{
                    fontFamily: dt.font,
                    fontSize: markerSize <= 32 ? "12px" : "15px",
                    fontWeight: 700,
                    color: dt.textSecondary,
                    userSelect: "none",
                  }}
                >
                  {firstLetter}
                </span>
              )}
            </div>
          </div>

          {/* Label below */}
          <div
            style={{
              position: "absolute",
              bottom: -18,
              left: "50%",
              transform: "translateX(-50%)",
              fontFamily: dt.font,
              fontSize: markerSize <= 32 ? "10px" : "11px",
              fontWeight: 700,
              color: dt.textPrimary,
              whiteSpace: "nowrap",
              textShadow: "0 1px 3px rgba(255,255,255,0.9)",
              userSelect: "none",
              letterSpacing: "0.3px",
            }}
          >
            {companyName}
          </div>
        </div>
      </OverlayViewF>
    );
  }
);

export default RfsOwnerMarker;
